import directives from './module';
import {Utils} from 'underscore-extensions';

type TabProps = {width: number, x: number};
class TabsCtrl implements ng.IComponentController {
    tabs: string[];
    labels: {[tab: string]: string};
    currentTab: string;
    defaultTab: string;
    changeTab: (obj: {currentTab: string}) => void;
    reloadOnChange: boolean;
    disableBorder: boolean;
    tabsProperties: ng.IPromise<TabProps>;
    transitionSet: boolean;
    path: string;
    firstRun: boolean;
    isMobileMode: boolean;
    cssSet: boolean;
    config?: {[tab: string]: boolean};

    /*@ngInject*/
    constructor(protected $rootScope: ng.IRootScopeService,
                protected $scope: ng.IScope,
                protected $location: ng.ILocationService,
                protected $state: ng.ui.IStateService,
                protected $timeout: ng.ITimeoutService,
                protected $element: ng.IAugmentedJQuery,
                protected $q: ng.IQService) {
        if (_.isUndefined(this.firstRun)) this.firstRun = true;
        if (_.isUndefined(this.isMobileMode)) this.isMobileMode = true;
        this.path = this.$state.current.name;
        this.configureTabs();
        if (!this.defaultTab && this.tabs && this.tabs.length > 0) {
            this.defaultTab = this.tabs[0];
        }
        this.changeTab      = this.changeTab || angular.noop;
        this.reloadOnChange = this.reloadOnChange || false;

        if (this.firstRun) {
            this.defaultTab && this.onTabChange(this.defaultTab);
        } else {
            this.currentTab = this.defaultTab;
        }
        if (!this.disableBorder) {
            Utils.scopedEvent('resize', this.$scope, _.throttle(() => this.setCss(true), 500));
        }

        const destroy = $rootScope.$on('user:login', this.configureTabs.bind(this));
        $scope.$on('$destroy', () => destroy());

    }

    $onChanges(onChangesObj: ng.IOnChangesObject) {
        this.setCss(true);
    }

    activateTab(tab: string) {
        this.onTabChange(tab);
    }

    isActive(tab: string) {
        return tab === this.currentTab;
    }

    getTabsProperties(cached = true): ng.IPromise<TabProps> {
        return cached && this.tabsProperties || this.computeTabsProperties();
    }

    getCurrent(cached = true) {
        return this.getTabsProperties(cached)
            .then(w => w[this.tabs.indexOf(this.currentTab)]);
    }

    computeTabsProperties() {
        this.tabsProperties = this.$q(resolve => this.$timeout(() => {
            resolve(_.map(this.$element[0].querySelectorAll(`.tab`), s => ({width: s.offsetWidth, x: s.offsetLeft})));
        }));
        return this.tabsProperties;
    }

    setCss(force = false) {
        this.cssSet = true;
        this.getCurrent(!force).then(tabProps => {
            if (!tabProps) return; //if tab is hidden
            const hr = this.$element[0].querySelector(`.hr`);
            if (!hr) return;

            const marginProp = `translateX(${tabProps.x}px)`;
            ['mozTransform', 'webkitTransform', 'transform'].map(prop => hr.style[prop] = marginProp);
            hr.style.width = tabProps.width + 'px';
            if (!this.transitionSet && !this.reloadOnChange) {
                this.transitionSet = true;
                this.$timeout(() => Utils.rAF(() =>
                    hr.style.transition = 'transform .3s, width .3s ease-in-out')
                );
            }
        });
    }

    onTabChange(currentTab: string) {
        if (_.contains(this.tabs, currentTab) && (!this.isActive(currentTab) || !this.cssSet)) {
            this.currentTab = currentTab;
            this.changeTab({currentTab});
            if (!this.disableBorder) {
                this.setCss();
            }
        }
    }

    protected configureTabs() {
        if (this.config && this.tabs) {
            this.tabs = this.tabs.filter(t => this.config[t] !== true);
        }
    }
}

class TabsSubPathCtrl extends TabsCtrl {
    parent: string;
    /*@ngInject*/
    constructor(protected $rootScope: ng.IRootScopeService,
                protected $scope: ng.IScope,
                protected $location: ng.ILocationService,
                protected $state: ng.ui.IStateService,
                protected $timeout: ng.ITimeoutService,
                protected $element: ng.IAugmentedJQuery,
                protected $q: ng.IQService) {
        super($rootScope, $scope, $location, $state, $timeout, $element, $q);
        this.parent       = (<any>$state.$current).parent.self.name || $state.current.name;
        const childStates = $state.get().map(s => s.name).filter(s => s.startsWith(this.parent) && s.indexOf('.') > 0);
        this.tabs         = childStates.map(s => this.stripParent(s));
        this.configureTabs();
        this.defaultTab = this.defaultTab || this.tabs[0];

        const hash = $location.hash();
        if (_.contains(this.tabs, hash)) {
            $location.path(this.addParent(hash, '/'));
            $location.hash('').replace();
        } else if (!_.contains(this.tabs, this.stripParent($state.current.name))) {
            $state.go(this.addParent(this.defaultTab), $state.params);
        }

        this.$scope.$watch(() => this.stripParent($state.current.name), this.onTabChange.bind(this));
    }

    $postLink() {
        this.$timeout(() => {
            _.each(this.$element[0].querySelectorAll('a'), el => {
                el.href = this.$state.href(this.addParent(el.id), this.$state.params);
            });
        });
    }

    activateTab(tab: string) {
        this.$state.go(this.addParent(tab), this.$state.params, {reload: this.reloadOnChange});
    }

    onTabChange(currentTab: string) {
        if (this.parent && this.tabs.indexOf(this.stripParent(this.$state.current.name)) !== -1) {
            this.$rootScope.setTitle(this.$state.current.name + '.title');
            super.onTabChange(currentTab);
        }
    }

    private stripParent(tab: string) {
        return tab.substring(this.parent.length + 1, tab.length);
    }

    private addParent(tab: string, separator = '.') {
        return this.parent + separator + tab;
    }
}


const directivesMap = {'tabs': TabsCtrl, 'tabsSubPath': TabsSubPathCtrl};
Object.keys(directivesMap).forEach(name => {
    directives.component(name, {
        controller: directivesMap[name],
        controllerAs: 'Tabs',
        bindings: {
            changeTab: '&',
            defaultTab: '@',
            tabs: '=?',
            reloadOnChange: '=?',
            currentTab: '=?',
            disableBorder: '=?',
            firstRun: '=?',
            config: '<?',
            labels: '<?',
            isMobileMode: '<?'
        },
        transclude: true,
        templateUrl: require('tabs.html')
    });
});



// WEBPACK FOOTER //
// ./app/assets/javascripts/directives/tabsDirective.ts